// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview Zod schema for the reviews VCA collection.
 *
 * PR/MR review objects (the top-level review submission, not individual line comments).
 * Line-level comments are stored in the `review_comments` collection.
 *
 * Identity tuple: (gitConnectionId, repoId, prNumber, reviewId)
 * ID prefix: grv_
 */

import { z } from 'zod'

export const reviewsSchema = z.object({
  id: z.string().max(100),
  orgId: z.string().max(100),
  workspaceId: z.string().max(100).optional(),

  // ── Git connection reference ──────────────────────────────────────────
  gitConnectionId: z.string().max(100),
  codebaseId: z.string().max(100).optional(),

  // ── Identity tuple ────────────────────────────────────────────────────
  repoId: z.string().max(200),
  /** FlowState pullrequests.id (FK) for cross-collection joins. */
  pullrequestId: z.string().max(100).optional(),
  /** PR/MR number within the repository (mirrors pullrequests.number). */
  prNumber: z.number().int(),
  /** Provider-assigned review identifier. */
  reviewId: z.string().max(200),

  // ── Review content ────────────────────────────────────────────────────
  /** Final review disposition. */
  state: z.enum(['approved', 'changes_requested', 'commented', 'dismissed']),
  /** Top-level review comment body (may be empty string). */
  body: z.string().optional(),
  authorLogin: z.string().max(200),

  // ── Timestamps ───────────────────────────────────────────────────────
  submittedAt: z.string().datetime(),

  // ── Provider-specific extras ──────────────────────────────────────────
  providerData: z.record(z.string(), z.unknown()).optional(),
})

export type ReviewsInput = z.infer<typeof reviewsSchema>
